import React, { useState, useEffect } from 'react';
import Message from '../component/Message';
import { easeOut, motion } from 'framer-motion';
import './MessagesBoardPage.scss';
import { getMessages, insertMessage } from '../../lib/neon';
import interact from "interactjs";

function MessagesBoardPage() {
    // State management for form inputs and messages
    const [name, setName] = useState('');
    const [message, setMessage] = useState('');
    const [messages, setMessages] = useState([]);

    // Set up message fetching and polling
    useEffect(() => {
        fetchMessages();
        // Poll for new messages every 5 seconds
        const interval = setInterval(fetchMessages, 5000);
        return () => clearInterval(interval);
    }, []);

    // Fetch messages from the database
    const fetchMessages = async () => {
        const { data, error } = await getMessages();
        if (error) {
            console.error('Error fetching messages:', error);
            return;
        }
        setMessages(data || []);
    };

    // Handle message submission
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!name.trim() || !message.trim()) return;

        // Calculate random position for the message
        // Position within 90% of width and 80% of remaining height after 100px top offset
        const x = Math.random() * 90 + 5; // 5% to 95% of width
        const topOffsetVh = (100 / window.innerHeight) * 100;
        const remainingHeightPercent = 90 - topOffsetVh;
        const y = (Math.random() * remainingHeightPercent) + topOffsetVh;

        const randomPosition = { x, y };

        // Insert new message into database
        const { error } = await insertMessage({
            name,
            message,
            position: randomPosition
        });

        if (error) {
            console.error('Error inserting message:', error);
            return;
        }

        // Reset form and refresh messages
        setName('');
        setMessage('');
        fetchMessages();
    };

    // Set up draggable functionality for messages
    useEffect(() => {
        const isMobile = window.innerWidth <= 768; // Standard mobile breakpoint

        interact(".draggable")
            .draggable({
                inertia: true,
                //touch: true,
                // Restrict movement within viewport bounds
                modifiers: [
                    interact.modifiers.restrictRect({
                        restriction: isMobile ? {
                            // Mobile restrictions - more limited area
                            top: 70, // 65 + 5
                            left: 5,
                            right: window.innerWidth - 5,
                            bottom: window.innerHeight - 45 // Higher bottom offset for mobile footer
                        } : {
                            // Desktop restrictions
                            top: 95, // 85 + 10
                            left: 10,
                            right: window.innerWidth - 10,
                            bottom: window.innerHeight - 10
                        },
                        endOnly: true,
                    }),
                ],
                autoScroll: true,
                listeners: {
                    move: dragMoveListener,
                },
            })
            .styleCursor(false);

        // Handle drag movement
        function dragMoveListener(event) {
            var target = event.target;
            var x = (parseFloat(target.getAttribute("data-x")) || 0) + event.dx;
            var y = (parseFloat(target.getAttribute("data-y")) || 0) + event.dy;

            // Update element position
            target.style.transform = "translate(" + x + "px, " + y + "px)";
            target.setAttribute("data-x", x.toString());
            target.setAttribute("data-y", y.toString());
        }

        window.dragMoveListener = dragMoveListener;
    }, []);

    // Calculate opacity based on message age
    const calculateOpacity = (createdAt) => {
        const now = new Date();
        const messageDate = new Date(createdAt);
        const ageInDays = (now - messageDate) / (1000 * 60 * 60 * 24);

        // Messages older than 6 months become invisible
        if (ageInDays > 180) return 0;

        // Linear fade out over 180 days
        const opacity = Math.max(0, 1 - (ageInDays / 180));
        return opacity;
    };

    // Filter and sort messages
    const filteredMessages = messages
        .filter(msg => {
            const ageInDays = (new Date() - new Date(msg.created_at)) / (1000 * 60 * 60 * 24);
            return ageInDays <= 180;
        })
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

    return (
        // Page container with fade animation
        /*    <motion.div
               className="messages-board-container"
               initial={{ opacity: 0 }}
               animate={{ opacity: 1 }}
               exit={{ opacity: 0 }}
               transition={{ duration: 0.25, ease: easeOut }}
           >
    */
        <div className="messages-board-container">
            {/* Message submission form */}
            <form onSubmit={handleSubmit} className="message-form" >
                <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="type your message"
                    required
                    maxLength={100}
                />
                <div className="name-submit-container">
                    <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="your name"
                        required
                        maxLength={25}
                    />
                    <button type="submit">send</button>
                </div>
            </form>

            {/* Messages display container */}

            <motion.div
                className="messages-container"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25, delay: 0.25, ease: easeOut }}
            >
                {filteredMessages.map((msg) => (
                    <Message
                        key={msg.id}
                        id={msg.id}
                        name={msg.name}
                        message={msg.message}
                        position={msg.position}
                        created_at={msg.created_at}
                        zIndex={msg.id}
                        style={{ opacity: calculateOpacity(msg.created_at) }}
                    />
                ))}
            </motion.div>

        </div>
    );
}

export default MessagesBoardPage;
